import { FC, useMemo, useState } from 'react';
import { clsx } from 'clsx';
import { IconButton, Menu, MenuItem, Typography } from '@mui/material';
import { Add, Check, MoreHoriz, Settings } from '@mui/icons-material';
import dayjs from 'dayjs';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as CalendarCheckIcon } from '../../../assets/tabs/task-tab/calendar-check.svg';
import { ReactComponent as ArrowUpDownIcon } from '../../../assets/tabs/task-tab/arrow-up-down.svg';
import {
  dataTrendPeriodAtom,
  selectedDateAtom,
  settingsSidebarStateAtom,
  sidebarOpenAtom,
  useTodoTasksContext,
} from '../../../data-access';
import { SIDEBAR_MENU } from '../../../pages/DashboardPage/SideBar/constants';
import { AddProjectDialog } from '../../Project';
import { useAddProject, useDropdownState } from '../../../hooks';
import { Path } from '../../../routing';
import { CustomDatePickerModal } from '../../DataView/ReDataView/DataHeader/CustomDatePickerModal';
import './TabHeader.scss';
import { useTranslation } from 'react-i18next';
import { formatDataDate } from '../../DataView/i18n';

export type TaskSortOption =
  | 'due_date_desc'
  | 'due_date_asc'
  | 'alphabetical'
  | 'priority'
  | 'manual';
export type TabHeaderMode = 'task' | 'routine' | 'data';

const SORT_OPTIONS: { value: TaskSortOption; labelKey: string }[] = [
  { value: 'manual', labelKey: 'task.header.sortOptions.manual' },
  { value: 'due_date_desc', labelKey: 'task.header.sortOptions.dueDateDesc' },
  { value: 'due_date_asc', labelKey: 'task.header.sortOptions.dueDateAsc' },
  { value: 'alphabetical', labelKey: 'task.header.sortOptions.alphabetical' },
  { value: 'priority', labelKey: 'task.header.sortOptions.priority' },
];

export interface TabHeaderProps {
  mode?: TabHeaderMode;
  onDateRangeClick?: () => void;
  taskSortOption?: TaskSortOption | null;
  onTaskSortChange?: (sortOption: TaskSortOption | null) => void;
}

const TabHeader: FC<TabHeaderProps> = ({
  mode = 'task',
  onDateRangeClick,
  taskSortOption,
  onTaskSortChange,
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isTaskMode = mode === 'task';
  const isDataMode = mode === 'data';
  const setSettingsSidebarState = useSetRecoilState(settingsSidebarStateAtom);
  const { todoProjects } = useTodoTasksContext();
  const { projectDialogVisible, isAddingProject, handleAddProject, handleProjectDialogVisible } =
    useAddProject();

  const [internalSortOption, setInternalSortOption] = useState<TaskSortOption | null>(
    'due_date_desc'
  );
  const [selectedDate, setSelectedDate] = useRecoilState(selectedDateAtom);
  const [dataTrendPeriod, setDataTrendPeriod] = useRecoilState(dataTrendPeriodAtom);
  const [isCustomDateModalOpen, setIsCustomDateModalOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useRecoilState(sidebarOpenAtom);

  const isSortControlled = taskSortOption !== undefined;
  const currentSortOption = isSortControlled ? taskSortOption : internalSortOption;

  const sortDropdown = useDropdownState();
  const menuDropdown = useDropdownState();
  const dateDropdown = useDropdownState();

  const today = dayjs();
  const yesterday = today.subtract(1, 'day');
  const thisWeekStart = today.subtract(today.day(), 'day');
  const lastWeekStart = today.subtract(today.day() + 7, 'day');

  const isToday = selectedDate.isSame(today, 'day') && dataTrendPeriod === 'daily';
  const isYesterday = selectedDate.isSame(yesterday, 'day') && dataTrendPeriod === 'daily';
  const isThisWeek = selectedDate.isSame(thisWeekStart, 'day') && dataTrendPeriod === 'weekly';
  const isLastWeek = selectedDate.isSame(lastWeekStart, 'day') && dataTrendPeriod === 'weekly';

  const dateRangeLabel = useMemo(() => {
    if (!isDataMode) {
      return t('data.common.today');
    }

    if (isToday) {
      return t('data.common.today');
    }
    if (isYesterday) {
      return t('data.common.yesterday');
    }
    if (isThisWeek) {
      return t('data.common.thisWeek');
    }
    if (isLastWeek) {
      return t('data.common.lastWeek');
    }

    if (dataTrendPeriod === 'weekly') {
      return `${formatDataDate(i18n.language, selectedDate.startOf('week').toDate(), {
        month: 'short',
        day: 'numeric',
      })} - ${formatDataDate(i18n.language, selectedDate.endOf('week').toDate(), {
        month: 'short',
        day: 'numeric',
      })}`;
    }

    return formatDataDate(i18n.language, selectedDate.toDate(), {
      month: 'short',
      day: 'numeric',
    });
  }, [
    dataTrendPeriod,
    i18n.language,
    isDataMode,
    isLastWeek,
    isThisWeek,
    isToday,
    isYesterday,
    selectedDate,
    t,
  ]);

  const handleSortSelect = (value: TaskSortOption) => {
    const nextSortOption = currentSortOption === value ? null : value;

    if (!isSortControlled) {
      setInternalSortOption(nextSortOption);
    }
    onTaskSortChange?.(nextSortOption);
    sortDropdown.handleCloseDropdown();
  };

  const handleOpenAddProjectDialog = () => {
    menuDropdown.handleCloseDropdown();
    handleProjectDialogVisible(true);
  };

  const handleOpenTodoSettings = () => {
    menuDropdown.handleCloseDropdown();
    setSettingsSidebarState({
      selectedParentKey: 'todos',
      expandedParentKeys: [],
    });
    navigate(`${Path.HOME}/settings`);
  };

  const handleDataDateSelect = (value: 'today' | 'yesterday' | 'this_week' | 'last_week') => {
    if (value === 'today') {
      setSelectedDate(dayjs());
      setDataTrendPeriod('daily');
    } else if (value === 'yesterday') {
      setSelectedDate(dayjs().subtract(1, 'day'));
      setDataTrendPeriod('daily');
    } else if (value === 'this_week') {
      const currentSunday = dayjs().subtract(dayjs().day(), 'day');
      setSelectedDate(currentSunday);
      setDataTrendPeriod('weekly');
    } else if (value === 'last_week') {
      const previousSunday = dayjs().subtract(dayjs().day() + 7, 'day');
      setSelectedDate(previousSunday);
      setDataTrendPeriod('weekly');
    }

    dateDropdown.handleCloseDropdown();
  };

  const handleDateButtonClick = (event: React.MouseEvent<HTMLElement>) => {
    if (isDataMode) {
      dateDropdown.handleOpenDropdown(event);
      return;
    }

    onDateRangeClick?.();
  };

  return (
    <header className="tab-header">
      <div className="tab-header__left-group">
        {!isDataMode && !sidebarOpen && (
          <button
            type="button"
            className="tab-header__sidebar-toggle"
            onClick={() => setSidebarOpen(true)}
            aria-label={t('data.common.openSidebar')}
          >
            <SIDEBAR_MENU.open title={t('data.common.openSidebar')} />
          </button>
        )}
        <button type="button" className="tab-header__date-range" onClick={handleDateButtonClick}>
          <CalendarCheckIcon
            className="tab-header__date-range__icon"
            style={{ color: 'var(--color-black)' }}
          />
          <Typography variant="subtitle2" className="tab-header__date-range__label">
            {dateRangeLabel}
          </Typography>
        </button>
      </div>

      {isTaskMode && (
        <div className="tab-header__actions">
          <IconButton
            className={clsx(
              'tab-header__action',
              sortDropdown.anchorEl && 'tab-header__action--open'
            )}
            onClick={sortDropdown.handleOpenDropdown}
            size="small"
            aria-label={t('task.header.sortBy')}
          >
            <ArrowUpDownIcon
              className="tab-header__action__icon"
              style={{ color: 'var(--color-black)' }}
            />
          </IconButton>

          <Menu
            anchorEl={sortDropdown.anchorEl}
            open={Boolean(sortDropdown.anchorEl)}
            onClose={sortDropdown.handleCloseDropdown}
            className="tab-header__sort-menu"
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <div className="tab-header__sort-menu__header">
              <Typography variant="body2" className="tab-header__sort-menu__header__text">
                {t('task.header.sortBy')}
              </Typography>
            </div>
            {SORT_OPTIONS.map((option) => (
              <MenuItem
                key={option.value}
                className={clsx('tab-header__sort-menu__item', {
                  'tab-header__sort-menu__item--selected': currentSortOption === option.value,
                })}
                onClick={() => handleSortSelect(option.value)}
              >
                <Typography variant="body2">{t(option.labelKey)}</Typography>
                {currentSortOption === option.value && (
                  <Check className="tab-header__sort-menu__item__selected-icon" fontSize="small" />
                )}
              </MenuItem>
            ))}
          </Menu>

          <IconButton
            className={clsx(
              'tab-header__action',
              menuDropdown.anchorEl && 'tab-header__action--open'
            )}
            onClick={menuDropdown.handleOpenDropdown}
            size="small"
            aria-label={t('task.header.taskSettings')}
          >
            <MoreHoriz className="tab-header__action__icon" />
          </IconButton>

          <Menu
            anchorEl={menuDropdown.anchorEl}
            open={Boolean(menuDropdown.anchorEl)}
            onClose={menuDropdown.handleCloseDropdown}
            className="tab-header__menu"
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <div className="tab-header__menu__header">
              <Typography variant="body2" className="tab-header__menu__header__text">
                {t('task.header.taskSettings')}
              </Typography>
            </div>
            <MenuItem className="tab-header__menu__item" onClick={handleOpenAddProjectDialog}>
              <Add className="tab-header__menu__item__icon" fontSize="small" />
              <Typography variant="body2">{t('task.project.addProject')}</Typography>
            </MenuItem>
            <MenuItem className="tab-header__menu__item" onClick={handleOpenTodoSettings}>
              <Settings className="tab-header__menu__item__icon" fontSize="small" />
              <Typography variant="body2">{t('task.header.manageSettings')}</Typography>
            </MenuItem>
          </Menu>
        </div>
      )}

      <Menu
        anchorEl={dateDropdown.anchorEl}
        open={isDataMode && Boolean(dateDropdown.anchorEl)}
        onClose={dateDropdown.handleCloseDropdown}
        className="tab-header__date-menu"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <div className="tab-header__date-menu__header">
          <Typography variant="body2" className="tab-header__date-menu__header__text">
            {t('data.common.dateRange')}
          </Typography>
        </div>

        <MenuItem
          className={clsx('tab-header__date-menu__item', {
            'tab-header__date-menu__item--selected': isToday,
          })}
          onClick={() => handleDataDateSelect('today')}
        >
          <Typography variant="body2">{t('data.common.today')}</Typography>
        </MenuItem>

        <MenuItem
          className={clsx('tab-header__date-menu__item', {
            'tab-header__date-menu__item--selected': isYesterday,
          })}
          onClick={() => handleDataDateSelect('yesterday')}
        >
          <Typography variant="body2">{t('data.common.yesterday')}</Typography>
        </MenuItem>

        <MenuItem
          className={clsx('tab-header__date-menu__item', {
            'tab-header__date-menu__item--selected': isThisWeek,
          })}
          onClick={() => handleDataDateSelect('this_week')}
        >
          <Typography variant="body2">{t('data.common.thisWeek')}</Typography>
        </MenuItem>

        <MenuItem
          className={clsx('tab-header__date-menu__item', {
            'tab-header__date-menu__item--selected': isLastWeek,
          })}
          onClick={() => handleDataDateSelect('last_week')}
        >
          <Typography variant="body2">{t('data.common.lastWeek')}</Typography>
        </MenuItem>

        <MenuItem
          className="tab-header__date-menu__item"
          onClick={() => {
            dateDropdown.handleCloseDropdown();
            setIsCustomDateModalOpen(true);
          }}
        >
          <Typography variant="body2">{t('data.common.custom')}</Typography>
        </MenuItem>
      </Menu>

      {isDataMode && (
        <CustomDatePickerModal
          isOpen={isCustomDateModalOpen}
          onClose={() => setIsCustomDateModalOpen(false)}
          selectedDate={selectedDate}
        />
      )}

      <AddProjectDialog
        isAdding={isAddingProject}
        visible={projectDialogVisible}
        existingProjectNames={todoProjects.map((project) => project.name)}
        onAdd={handleAddProject}
        onClose={() => handleProjectDialogVisible(false)}
      />
    </header>
  );
};

export default TabHeader;
